import { useEffect, useState } from 'react'

export type ThemeId = 'parchment' | 'broadsheet'

export const THEMES: Array<{ id: ThemeId; label: string; description: string }> = [
  { id: 'parchment',  label: 'Parchment',  description: 'Editorial — Cormorant serif, saffron + ink palette, paper texture' },
  { id: 'broadsheet', label: 'Broadsheet', description: 'Data-journalism — white background, restrained blue, hairline rules, more whitespace' }
]

const STORAGE_KEY = 'bhavan-atlas:theme'

function isThemeId(v: unknown): v is ThemeId {
  return typeof v === 'string' && THEMES.some(t => t.id === v)
}

function readInitial(): ThemeId {
  if (typeof window === 'undefined') return 'parchment'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (isThemeId(stored)) return stored
  // Also support ?theme=<id> for shareable URLs.
  const fromUrl = new URLSearchParams(window.location.search).get('theme')
  if (isThemeId(fromUrl)) return fromUrl
  return 'parchment'
}

// =============================================================================
// Shared module-level state. Every useTheme() consumer subscribes to changes
// so the entire app re-renders in lockstep when the theme flips. Without this,
// each useState() instance held its own copy of the theme — the picker would
// update App but MapView (which has its own useTheme call) would lag behind,
// leaving the map in the previous palette.
// =============================================================================
let currentTheme: ThemeId = readInitial()
const listeners = new Set<(t: ThemeId) => void>()

// Apply on module load so the data-theme attribute is right before first paint
// (avoids a flash of the wrong theme during hydration).
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('data-theme', currentTheme)
}

function applyTheme(id: ThemeId) {
  if (id === currentTheme) return
  currentTheme = id
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', id)
  }
  try { window.localStorage.setItem(STORAGE_KEY, id) } catch { /* quota / disabled — ignore */ }
  listeners.forEach(l => l(id))
}

export function useTheme() {
  const [theme, setLocal] = useState<ThemeId>(currentTheme)
  useEffect(() => {
    const listener = (id: ThemeId) => setLocal(id)
    listeners.add(listener)
    // If theme changed between mount and effect, catch up.
    if (currentTheme !== theme) setLocal(currentTheme)
    return () => { listeners.delete(listener) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return { theme, setTheme: applyTheme }
}
